// Segmented controls for Manufacturing → Models.
// Top slide: Models | Variants | Slots | Uses. Under Models: Create or edit | View.

export type ManufacturingModelsWorkspace = "models" | "variants" | "slots" | "uses";
export type ManufacturingModelsMode = "create" | "view";

const WORKSPACE_TABS: ReadonlyArray<{ id: ManufacturingModelsWorkspace; label: string }> = [
  { id: "models", label: "Models" },
  { id: "variants", label: "Variants" },
  { id: "slots", label: "Slots" },
  { id: "uses", label: "Part uses" },
];

const MODE_TABS: ReadonlyArray<{ id: ManufacturingModelsMode; label: string }> = [
  { id: "create", label: "Create or edit" },
  { id: "view", label: "View" },
];

function SegmentedTabs<T extends string>({
  ariaLabel,
  tabs,
  value,
  onChange,
}: {
  ariaLabel: string;
  tabs: ReadonlyArray<{ id: T; label: string }>;
  value: T;
  onChange: (next: T) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="inline-flex flex-nowrap overflow-x-auto rounded-lg bg-gray-100 p-0.5 text-sm dark:bg-zinc-800"
    >
      {tabs.map((tab) => {
        const active = tab.id === value;
        return (
          <button
            key={tab.id}
            role="tab"
            aria-selected={active}
            type="button"
            onClick={() => onChange(tab.id)}
            className={
              "shrink-0 whitespace-nowrap rounded-md px-3 py-1 text-sm font-semibold transition-colors " +
              (active
                ? "bg-white text-gray-900 shadow-sm dark:bg-zinc-900 dark:text-zinc-100"
                : "text-gray-600 hover:text-gray-900 dark:text-zinc-400 dark:hover:text-zinc-200")
            }
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

export function ManufacturingModelsWorkspaceToggle({
  workspace,
  onChange,
}: {
  workspace: ManufacturingModelsWorkspace;
  onChange: (next: ManufacturingModelsWorkspace) => void;
}) {
  return (
    <SegmentedTabs
      ariaLabel="Models workspace"
      tabs={WORKSPACE_TABS}
      value={workspace}
      onChange={onChange}
    />
  );
}

export function ManufacturingModelsModeToggle({
  mode,
  onChange,
}: {
  mode: ManufacturingModelsMode;
  onChange: (next: ManufacturingModelsMode) => void;
}) {
  return (
    <SegmentedTabs ariaLabel="Models mode" tabs={MODE_TABS} value={mode} onChange={onChange} />
  );
}
